import axios from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { getUserIdFromToken } from '../utils/authUtils';
import { getApplicationIdWithFallback } from '../utils/moduleUtils';

/** Normalize selected_context_data.site_info to a non-empty array for PDF, or null. */
function normalizeReportSiteInfo(siteInfo) {
  if (siteInfo == null) {
    return null;
  }
  if (Array.isArray(siteInfo) && siteInfo.length > 0) {
    return siteInfo;
  }
  if (typeof siteInfo === 'object' && !Array.isArray(siteInfo)) {
    return [siteInfo];
  }
  return null;
}

/** PDF/report labels only; does not attach full selected_context_data to messages. */
function pickReportFieldsFromSelectedContext(ctx) {
  if (!ctx || typeof ctx !== 'object') {
    return {};
  }
  const name =
    typeof ctx.customer_name === 'string' ? ctx.customer_name.trim() : '';
  const locRaw = ctx.customer_location ?? ctx.location;
  const loc = typeof locRaw === 'string' ? locRaw.trim() : '';
  const sites = normalizeReportSiteInfo(ctx.site_info);
  return {
    ...(name ? { reportCustomerName: name } : {}),
    ...(loc ? { reportLocation: loc } : {}),
    ...(sites ? { reportSiteInfo: sites } : {}),
  };
}

export const ChatQueryPayload = {
  application_id: '',
  user_id: '',
  question: '',
  category: '',
  sub_category: '',
  chat_id: '',
  is_new_session: false,
  language: '',
}

export const queryChat = async (payload) => {
  const response = await axios.post(API_ENDPOINTS.smartDocs, payload);
  return response.data;
};

export const SmartDocsStreamCallbacks = {
  onChunk: (partialContent) => {},
  onHeaderCreated: (header, conversationId) => {},
  onStreamComplete: (conversationId, pairId) => {},
}

export const streamSmartDocsQuery = async (
  payload,
  callbacks
) => {
  // For streaming, we still need to use fetch as axios doesn't handle SSE streams well
  const accessToken = localStorage.getItem('access_token');
  
  const response = await fetch(API_ENDPOINTS.smartDocs, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
    },
    body: JSON.stringify(payload),
  });

  if (!response.body) {
    throw new Error('Streaming not supported');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // carry over partial line

    for (const line of lines) {
      if (line.startsWith('data:')) {
        try {
          const jsonStr = line.replace(/^data:\s*/, '');
          const parsed = JSON.parse(jsonStr);

          if (parsed.text) {
            callbacks.onChunk(parsed.text);
          } else if (parsed.type === 'header_created' && parsed.header) {
            callbacks.onHeaderCreated?.(parsed.header, parsed.conversation_id);
          } else if (parsed.type === 'stream_complete') {
            callbacks.onStreamComplete?.(parsed.conversation_id, parsed.pair_id);
          }
        } catch (e) {
          console.error('Failed to parse SSE JSON line:', line, e);
        }
      }
    }
  }
};

export const fetchChatFeedback = async (chatId, moduleKey) => {
  const userId = getUserIdFromToken();
  const applicationId = getApplicationIdWithFallback(moduleKey);
  
  if (!userId) {
    throw new Error('User ID not found in token');
  }
  if (!applicationId) {
    throw new Error('Application ID not found in environment');
  }

  try {
    const response = await axios.get(API_ENDPOINTS.chatFeedback, {
      params: {
        application_id: applicationId,
        chat_id: chatId,
        user_id: userId,
      }
    });

    return response.data.data; // returns array of feedback objects
  } catch (error) {
    // Handle 404 - no feedback found
    if (error.response?.status === 404) {
      // console.log('No feedback found for chat:', chatId);
      return []; // return empty array when no feedback exists
    }
    // Re-throw other errors
    throw error;
  }
};

export const fetchChatMessagesWithFeedback = async (chatId, moduleKey) => {
  const userId = getUserIdFromToken();
  const applicationId = getApplicationIdWithFallback(moduleKey);
  
  if (!userId) {
    throw new Error('User ID not found in token');
  }
  if (!applicationId) {
    throw new Error('Application ID not found in environment');
  }

  // Fetch both messages and feedback in parallel
  const [messagesResponse, feedbackResponse] = await Promise.all([
    axios.post(API_ENDPOINTS.chatMessages, {
      user_id: userId,
      chat_id: chatId,
    }),
    axios.get(API_ENDPOINTS.chatFeedback, {
      params: {
        application_id: applicationId,
        chat_id: chatId,
        user_id: userId,
      }
    }).catch((error) => {
      // Handle 404 - no feedback found
      if (error.response?.status === 404) {
        // console.log('No feedback found for chat:', chatId);
        return { data: { data: [] } }; // return structure matching successful response
      }
      throw error; // Re-throw other errors
    })
  ]);

  const rawMessages = messagesResponse.data.data;
  const feedbackData = feedbackResponse.data.data;

  // Get the application_id from the first message if available
  const messageApplicationId = rawMessages && rawMessages.length > 0 ? rawMessages[0].application_id : null;
  
  // Get category and subcategory from the first message if available
  const messageCategory = rawMessages && rawMessages.length > 0 ? rawMessages[0].category : null;
  const messageSubcategory = rawMessages && rawMessages.length > 0 ? rawMessages[0].sub_category : null;

  // Create a map of feedback by pair_id for quick lookup
  const feedbackMap = new Map();
  feedbackData.forEach((feedback) => {
    feedbackMap.set(feedback.pair_id, feedback);
  });

  // Format messages and include feedback data
  const formatted = rawMessages.flatMap((item) => {
    const feedback = feedbackMap.get(item.pair_id);
    const reportFields = pickReportFieldsFromSelectedContext(item.selected_context_data);

    return [
      { 
        role: 'user', 
        content: item.user_message, 
        pairId: item.pair_id 
      },
      {
        role: 'assistant',
        content: item.ai_response,
        citations: item.citation || [],
        images: item.images || item.ai_response_images || [],
        pairId: item.pair_id,
        ...reportFields,
        feedback: feedback ? {
          hasThumbsUp: feedback.feedback === true,
          hasThumbsDown: feedback.feedback === false,
          feedbackText: feedback.feedback_text || '',
          timestamp: feedback.timestamp
        } : null,
      },
    ];
  });

  return {
    messages: formatted,
    messageApplicationId,
    messageCategory,
    messageSubcategory
  };
};

export const submitChatFeedback = async (
  chatId,
  pairId,
  feedback,
  feedbackText = '',
  isUpdate = false,
  moduleKey
) => {
  const userId = getUserIdFromToken();
  const applicationId = getApplicationIdWithFallback(moduleKey);
  
  if (!userId) {
    throw new Error('User ID not found in token');
  }
  if (!applicationId) {
    throw new Error('Application ID not found in environment');
  }

  const payload = {
    application_id: applicationId,
    chat_id: chatId,
    pair_id: pairId,
    user_id: userId,
    feedback: feedback,
    feedback_text: feedbackText,
  };

  const method = isUpdate ? 'put' : 'post';
  const response = await axios[method](API_ENDPOINTS.chatFeedback, payload);
  
  return response.data;
};

export async function fetchChatHeaders(
  applicationId,
  userId,
  page = 1,
  pageSize = 10
) {
  const response = await axios.post(
    `${API_ENDPOINTS.chatHeaders}?page=${page}&page_size=${pageSize}`,
    {
      user_id: userId,
      application_id: applicationId,
    }
  );
  return response.data; // returns the full response including pagination info
}
